import React, {Suspense, lazy} from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
} from "react-router-dom";
import "./App.css";
import {useSelector} from "react-redux";
import HeaderComponent from "./components/Header/Header";
import BreadcumbComponent from "./components/Breadcumb/BreadcumbComponent";

const Login = lazy(() => import("./pages/Login"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Dashboard = lazy(() => import("./pages/Dashboard"));

// Inbound
const PurchaseRequisition = lazy(() =>
  import("./pages/Inbound/PurchaseRequisition/PRList")
);
const CreatePR = lazy(() =>
  import("./pages/Inbound/PurchaseRequisition/PRCreate")
);
const DetailPR = lazy(() =>
  import("./pages/Inbound/PurchaseRequisition/PRDetail")
);
const PurchaseOrder = lazy(() =>
  import("./pages/Inbound/PurchaseOrder/POList")
);
const CreatePO = lazy(() => import("./pages/Inbound/PurchaseOrder/POCreate"));
const DetailPO = lazy(() => import("./pages/Inbound/PurchaseOrder/PODetail"));
const QualityCheck = lazy(() =>
  import("./pages/Inbound/QualityCheck/QualityCheck")
);
const ReceivingReport = lazy(() =>
  import("./pages/Inbound/ReceivingReport/RRList")
);
const SubmitRR = lazy(() => import("./pages/Inbound/ReceivingReport/RRSubmit"));
const UpdateRR = lazy(() => import("./pages/Inbound/ReceivingReport/RRUpdate"));
const StoreIn = lazy(() => import("./pages/Inbound/StoreIn/StoreIn"));

// Outbound
const SalesOrder = lazy(() => import("./pages/Outbound/SalesOrder/SOList"));
const SalesOrderDetail = lazy(() =>
  import("./pages/Outbound/SalesOrder/SODetail")
);
const CreateSO = lazy(() => import("./pages/Outbound/SalesOrder/SOCreate"));
const DeliveryOrder = lazy(() =>
  import("./pages/Outbound/DeliveryOrder/DOList")
);
const DeliveryOrderDetail = lazy(() =>
  import("./pages/Outbound/DeliveryOrder/DODetail")
);
const CreateDO = lazy(() => import("./pages/Outbound/DeliveryOrder/DOCreate"));
const DOApproval = lazy(() => import("./pages/Outbound/DOApproval/DOApproval"));
const DOApprovalDetail = lazy(() =>
  import("./pages/Outbound/DOApproval/DOApprovalDetail")
);
const PickPack = lazy(() => import("./pages/Outbound/Cutstock/PickPack"));
const WalkOrder = lazy(() => import("./pages/Outbound/Walk Order/WalkOrder"));

// Inventory
const InventoryList = lazy(() => import("./pages/Inventory/InventoryList"));

// Report
const InventoryBalance = lazy(() => import("./pages/Report/InventoryBalance"));
const InventoryAging = lazy(() => import("./pages/Report/InventoryAging"));
const InventoryExpired = lazy(() => import("./pages/Report/InventoryExpired"));
const TransactionInventory = lazy(() =>
  import("./pages/Report/TransactionInventory")
);
const TransactionInventoryDetail = lazy(() =>
  import("./pages/Report/TransactionInventoryDetail")
);

// Adjustment
const AdjustmentQuantity = lazy(() =>
  import("./pages/Adjustment/AdjustmentQuantity")
);
const AdjustmentQuantityDetail = lazy(() =>
  import("./pages/Adjustment/AdjustmentQuantityDetail")
);

const Invoice = lazy(() => import("./pages/Invoice/Invoice"));
const SalesInvoice = lazy(() =>
  import("./pages/Invoice/SalesInvoice/SalesInvoiceList")
);
const CreateSalesInvoice = lazy(() =>
  import("./pages/Invoice/SalesInvoice/SalesInvoiceCreate")
);
const SalesInvoiceDetail = lazy(() =>
  import("./pages/Invoice/SalesInvoice/SalesInvoiceDetail")
);

// Master Data
const Category = lazy(() => import("./pages/MasterData/Category"));
const Product = lazy(() => import("./pages/MasterData/Product"));
const Customer = lazy(() => import("./pages/MasterData/Customer"));
const Supplier = lazy(() => import("./pages/MasterData/Supplier"));
const Location = lazy(() => import("./pages/MasterData/Location"));
const Shipping = lazy(() => import("./pages/MasterData/Shipping"));

// User Management
const UserManagement = lazy(() =>
  import("./pages/UserManagement/UserManagement")
);

const WebhookList = lazy(() => import("./pages/Profile/Webhook/WebhookList"));
const Settings = lazy(() => import("./pages/Profile/Settings"));
const Unauthorized = lazy(() => import("./pages/Unauthorized"));
const NotFound = lazy(() => import("./pages/NotFound"));

function App() {
  const PrivateRoute = ({element: Component, allowedRole, ...rest}) => {
    const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
    const userRole = useSelector((state) => state.auth?.user?.data?.roles);

    if (!isAuthenticated) {
      return <Navigate to="/login" />;
    }

    if (userRole?.includes("root")) {
      return <Component {...rest} />;
    } else {
      // return <Component {...rest} />;
      return userRole?.some((r) => allowedRole?.includes(r)) ? (
        <Component {...rest} />
      ) : (
        <Navigate to="/unauthorized" />
      );
    }
  };

  return (
    <Router>
      <Suspense fallback={<div></div>}>
        <HeaderComponent />
        <BreadcumbComponent />
        <div className="mx-6">
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route
              path="/"
              element={
                <PrivateRoute
                  element={Dashboard}
                  allowedRole={["root", "admin", "staff"]}
                />
              }
            />

            {/* Inbound */}
            <Route
              path="/purchase-requisition"
              element={
                <PrivateRoute
                  element={PurchaseRequisition}
                  allowedRole={["root", "admin"]}
                />
              }
            />
            <Route
              path="/purchase-requisition/:id"
              element={
                <PrivateRoute
                  element={DetailPR}
                  allowedRole={["root", "admin"]}
                />
              }
            />
            <Route
              path="/purchase-requisition/create-pr"
              element={
                <PrivateRoute
                  element={CreatePR}
                  allowedRole={["root", "admin"]}
                />
              }
            />
            <Route
              path="/purchase-requisition/update-pr/:id"
              element={
                <PrivateRoute
                  element={CreatePR}
                  allowedRole={["root", "admin"]}
                />
              }
            />
            <Route
              path="/purchase-order"
              element={
                <PrivateRoute
                  element={PurchaseOrder}
                  allowedRole={["root", "admin", "staff"]}
                />
              }
            />
            <Route
              path="/purchase-order/create-po"
              element={
                <PrivateRoute
                  element={CreatePO}
                  allowedRole={["root", "admin"]}
                />
              }
            />
            <Route
              path="/purchase-order/update-po/:id"
              element={
                <PrivateRoute
                  element={CreatePO}
                  allowedRole={["root", "admin"]}
                />
              }
            />
            <Route
              path="/purchase-order/:id"
              element={
                <PrivateRoute
                  element={DetailPO}
                  allowedRole={["root", "admin", "staff"]}
                />
              }
            />
            <Route
              path="/quality-check"
              element={
                <PrivateRoute
                  element={QualityCheck}
                  allowedRole={["root", "admin", "staff"]}
                />
              }
            />
            <Route
              path="/receiving-report"
              element={
                <PrivateRoute
                  element={ReceivingReport}
                  allowedRole={["root", "admin", "staff"]}
                />
              }
            />
            <Route
              path="/receiving-report/submit-rr"
              element={
                <PrivateRoute
                  element={SubmitRR}
                  allowedRole={["root", "admin", "staff"]}
                />
              }
            />
            <Route
              path="/receiving-report/:id"
              element={
                <PrivateRoute
                  element={UpdateRR}
                  allowedRole={["root", "admin", "staff"]}
                />
              }
            />
            <Route
              path="/put-away"
              element={
                <PrivateRoute
                  element={StoreIn}
                  allowedRole={["root", "admin", "staff"]}
                />
              }
            />
            {/* <Route path="/store-in/:id" element={<PrivateRoute element={ReceivingReport} />} /> */}

            {/* Outbound */}
            <Route
              path="/sales-order"
              element={
                <PrivateRoute
                  element={SalesOrder}
                  allowedRole={["root", "admin", "staff"]}
                />
              }
            />
            <Route
              path="/sales-order/:id"
              element={
                <PrivateRoute
                  element={SalesOrderDetail}
                  allowedRole={["root", "admin", "staff"]}
                />
              }
            />
            <Route
              path="/sales-order/create-so"
              element={
                <PrivateRoute
                  element={CreateSO}
                  allowedRole={["root", "admin"]}
                />
              }
            />
            <Route
              path="/sales-order/update-so/:id"
              element={
                <PrivateRoute
                  element={CreateSO}
                  allowedRole={["root", "admin"]}
                />
              }
            />
            <Route
              path="/delivery-order"
              element={
                <PrivateRoute
                  element={DeliveryOrder}
                  allowedRole={["root", "admin", "staff"]}
                />
              }
            />
            <Route
              path="/delivery-order/:id"
              element={
                <PrivateRoute
                  element={DeliveryOrderDetail}
                  allowedRole={["root", "admin", "staff"]}
                />
              }
            />
            <Route
              path="/delivery-order/create-do"
              element={
                <PrivateRoute
                  element={CreateDO}
                  allowedRole={["root", "admin", "staff"]}
                />
              }
            />
            <Route
              path="/delivery-order/update-do/:id"
              element={
                <PrivateRoute
                  element={CreateDO}
                  allowedRole={["root", "admin", "staff"]}
                />
              }
            />
            {/* <Route path="/delivery-order/approval-list" element={<PrivateRoute element={DOApproval} />} /> */}
            {/* <Route path="/delivery-order/approval-detail/:id" element={<PrivateRoute element={DOApprovalDetail} />} /> */}
            <Route
              path="/pickpack"
              element={
                <PrivateRoute
                  element={PickPack}
                  allowedRole={["root", "admin", "staff"]}
                />
              }
            />
            <Route
              path="/walk-order"
              element={
                <PrivateRoute
                  element={WalkOrder}
                  allowedRole={["root", "admin", "staff"]}
                />
              }
            />

            {/* Inventory */}
            <Route
              path="/inventory"
              element={
                <PrivateRoute
                  element={InventoryList}
                  allowedRole={["root", "admin", "staff"]}
                />
              }
            />

            {/* Report */}
            <Route
              path="/report-inventory-balance"
              element={
                <PrivateRoute
                  element={InventoryBalance}
                  allowedRole={["root", "admin", "staff"]}
                />
              }
            />
            <Route
              path="/report-inventory-aging"
              element={
                <PrivateRoute
                  element={InventoryAging}
                  allowedRole={["root", "admin", "staff"]}
                />
              }
            />
            <Route
              path="/report-inventory-exp-date"
              element={
                <PrivateRoute
                  element={InventoryExpired}
                  allowedRole={["root", "admin", "staff"]}
                />
              }
            />
            <Route
              path="/report-transaction-inventory"
              element={
                <PrivateRoute
                  element={TransactionInventory}
                  allowedRole={["root", "admin", "staff"]}
                />
              }
            />
            <Route
              path="/report-transaction-inventory-detail"
              element={
                <PrivateRoute
                  element={TransactionInventoryDetail}
                  allowedRole={["root", "admin", "staff"]}
                />
              }
            />

            {/* Adjustment */}
            <Route
              path="/adjustment-quantity"
              element={
                <PrivateRoute
                  element={AdjustmentQuantity}
                  allowedRole={["root", "admin", "staff"]}
                />
              }
            />
            <Route
              path="/adjustment-quantity/:id"
              element={
                <PrivateRoute
                  element={AdjustmentQuantityDetail}
                  allowedRole={["root", "admin", "staff"]}
                />
              }
            />

            <Route
              path="/invoice"
              element={
                <PrivateRoute
                  element={Invoice}
                  allowedRole={["root", "admin", "staff"]}
                />
              }
            />
            <Route
              path="/sales-invoice"
              element={
                <PrivateRoute
                  element={SalesInvoice}
                  allowedRole={["root", "admin", "staff"]}
                />
              }
            />
            <Route
              path="/sales-invoice/create"
              element={
                <PrivateRoute
                  element={CreateSalesInvoice}
                  allowedRole={["root", "admin", "staff"]}
                />
              }
            />
            <Route
              path="/sales-invoice/:id/:doid"
              element={
                <PrivateRoute
                  element={SalesInvoiceDetail}
                  allowedRole={["root", "admin", "staff"]}
                />
              }
            />
            <Route
              path="/sales-invoice/update/:id/:doid"
              element={
                <PrivateRoute
                  element={CreateSalesInvoice}
                  allowedRole={["root", "admin", "staff"]}
                />
              }
            />

            {/* Master Data */}
            <Route
              path="/category"
              element={
                <PrivateRoute
                  element={Category}
                  allowedRole={["root", "admin", "staff"]}
                />
              }
            />
            <Route
              path="/product"
              element={
                <PrivateRoute
                  element={Product}
                  allowedRole={["root", "admin", "staff"]}
                />
              }
            />
            <Route
              path="/customer"
              element={
                <PrivateRoute
                  element={Customer}
                  allowedRole={["root", "admin", "staff"]}
                />
              }
            />
            <Route
              path="/supplier"
              element={
                <PrivateRoute
                  element={Supplier}
                  allowedRole={["root", "admin", "staff"]}
                />
              }
            />
            <Route
              path="/location"
              element={
                <PrivateRoute
                  element={Location}
                  allowedRole={["root", "admin", "staff"]}
                />
              }
            />
            <Route
              path="/shipping"
              element={
                <PrivateRoute
                  element={Shipping}
                  allowedRole={["root", "admin", "staff"]}
                />
              }
            />

            {/* User Management */}
            <Route
              path="/user-management"
              element={
                <PrivateRoute element={UserManagement} allowedRole={["root"]} />
              }
            />

            <Route
              path="/webhook"
              element={
                <PrivateRoute
                  element={WebhookList}
                  allowedRole={["root", "admin"]}
                />
              }
            />
            <Route
              path="/app-settings"
              element={
                <PrivateRoute
                  element={Settings}
                  allowedRole={["root", "admin"]}
                />
              }
            />
            <Route path="/unauthorized" element={<Unauthorized />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </div>
      </Suspense>
    </Router>
  );
}

export default App;
